import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Search, Loader2, FlaskConical, Package, Clock, Home, Footprints, Droplet,
  ChevronLeft, ChevronRight, Pencil, Lock, Info, Check,
} from "lucide-react";
import { getLabTests } from "../../services/diagnosticLabsService";
import TestEditForm from "./TestEditForm";

const TYPE_TABS = [
  { value: "", label: "All" },
  { value: "test", label: "Tests" },
  { value: "package", label: "Packages" },
];
const PAGE_SIZE = 20;

const Chip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-[11px]">
    {Icon && <Icon className="w-3 h-3" />} {children}
  </span>
);

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// `loadTests(params)` (optional) swaps the data source — e.g. a chain test list —
// and must be a stable callback. Without it the drawer reads the lab's tests.
// `onSaveTest(test, payload)` (optional) enables inline editing; it must resolve
// to the saved test. `editNote` explains the scope of an edit; `readOnlyNote`
// explains why editing is unavailable (e.g. a chain-managed lab).
const TestsDrawer = ({ open, onClose, role, labId, labName, loadTests, subtitle, onSaveTest, editNote, readOnlyNote }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDirty, setEditDirty] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const canEdit = !!onSaveTest;

  useEffect(() => {
    if (!open) { setEditingId(null); setEditDirty(false); setDiscardPrompt(false); }
  }, [open]);

  // Never lose an unsaved edit silently: closing with changes asks first.
  const requestClose = () => {
    if (editingId && editDirty) setDiscardPrompt(true);
    else onClose();
  };

  const saveTest = async (test, payload) => {
    const saved = await onSaveTest(test, payload);
    setItems((prev) => prev.map((row) => (row.id === test.id ? { ...row, ...saved } : row)));
    setEditingId(null);
    setEditDirty(false);
    setSavedId(test.id);
    setTimeout(() => setSavedId((id) => (id === test.id ? null : id)), 2500);
  };

  // Reset paging whenever the drawer opens or the filters change.
  useEffect(() => { setPage(1); }, [open, search, type]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { search, type, page, items: PAGE_SIZE };
        const resp = loadTests ? await loadTests(params) : await getLabTests(role, labId, params);
        const data = resp?.msg || resp;
        setItems(data?.items || []);
        setCount(data?.pagination?.total || 0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, search, type, page, role, labId, loadTests]);

  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            onClick={requestClose}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full max-w-2xl bg-gray-50 h-full shadow-2xl flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-white border-b border-gray-100 flex items-center justify-between gap-3 shrink-0">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900">Tests &amp; Packages</h3>
                <p className="text-xs text-gray-400 truncate">{subtitle || labName} · {count} item{count === 1 ? "" : "s"}</p>
              </div>
              <button onClick={requestClose} aria-label="Close"
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Toolbar */}
            <div className="px-6 py-3 bg-white border-b border-gray-100 flex flex-col sm:flex-row gap-3 sm:items-center shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or category…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                />
              </div>
              <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gray-100 shrink-0">
                <span className="pl-2 pr-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 select-none">Type</span>
                {TYPE_TABS.map((t) => (
                  <button key={t.value} onClick={() => setType(t.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${type === t.value ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {(canEdit || readOnlyNote) && (
              <div className={`px-6 py-2 border-b text-[11px] flex items-start gap-2 shrink-0 ${canEdit ? "bg-[#F3FBFB] border-[#E3EEEE] text-[#0F5E5E]" : "bg-slate-50 border-gray-100 text-slate-600"}`}>
                {canEdit ? <Info className="w-3.5 h-3.5 mt-px shrink-0" /> : <Lock className="w-3.5 h-3.5 mt-px shrink-0" />}
                <span>{canEdit ? (editNote || "Edits go live immediately. The next sheet upload replaces the whole menu, including edits.") : readOnlyNote}</span>
              </div>
            )}

            <AnimatePresence>
              {discardPrompt && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 shrink-0 overflow-hidden">
                  <span className="text-xs text-amber-800">You have unsaved changes to a test.</span>
                  <span className="flex gap-2">
                    <button onClick={() => setDiscardPrompt(false)} className="px-2.5 py-1 text-xs font-medium text-gray-700 rounded-md hover:bg-white">Keep editing</button>
                    <button onClick={() => { setDiscardPrompt(false); setEditingId(null); setEditDirty(false); onClose(); }}
                      className="px-2.5 py-1 text-xs font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700">Discard &amp; close</button>
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2.5">
              {loading ? (
                <div className="py-16 flex justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : items.length === 0 ? (
                <div className="py-16 text-center text-gray-400 text-sm">
                  {search || type ? "No tests match your filters." : "No tests parsed from this sheet yet."}
                </div>
              ) : (
                items.map((t) => {
                  const mrp = Number(t.mrp);
                  const price = Number(t.ayushpay_price);
                  const diff = mrp > price ? mrp - price : 0;
                  const disc = mrp > price && mrp > 0 ? Math.round((diff / mrp) * 100) : 0;
                  if (editingId === t.id) {
                    return (
                      <TestEditForm
                        key={t.id}
                        test={t}
                        scopeNote={editNote ? "Applies to the whole list." : "Applies to this lab's menu."}
                        onDirtyChange={setEditDirty}
                        onCancel={() => { setEditingId(null); setEditDirty(false); }}
                        onSave={(payload) => saveTest(t, payload)}
                      />
                    );
                  }
                  return (
                    <div key={t.id} className={`group bg-white rounded-xl border p-4 hover:border-blue-200 hover:shadow-sm transition-all ${savedId === t.id ? "border-emerald-300 ring-2 ring-emerald-100" : "border-gray-100"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${t.type === "package" ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600"}`}>
                              {t.type === "package" ? <Package className="w-3.5 h-3.5" /> : <FlaskConical className="w-3.5 h-3.5" />}
                            </span>
                            <h4 className="font-semibold text-gray-900 truncate">{t.name}</h4>
                            {savedId === t.id && (
                              <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[10px] font-semibold">
                                <Check className="w-3 h-3" /> Saved
                              </span>
                            )}
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => setEditingId(t.id)}
                                disabled={!!editingId}
                                aria-label={`Edit ${t.name}`}
                                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-[#0F4C81] hover:bg-[#EAF2FA] md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 disabled:!opacity-0 transition-opacity"
                              >
                                <Pencil className="w-3 h-3" /> Edit
                              </button>
                            )}
                          </div>
                          {t.aliases?.length > 0 && (
                            <p className="mt-1 text-[11px] text-gray-400 truncate">{t.aliases.join(", ")}</p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {t.category && <Chip>{t.category}</Chip>}
                            {t.fasting_required && <Chip icon={Droplet}>Fasting</Chip>}
                            {t.tat_in_hours ? <Chip icon={Clock}>{t.tat_in_hours}h TAT</Chip> : null}
                            {t.home_collection && <Chip icon={Home}>Home collection</Chip>}
                            {t.walk_in && <Chip icon={Footprints}>Walk-in</Chip>}
                          </div>
                          {t.description && <p className="mt-2 text-xs text-gray-500 line-clamp-2">{t.description}</p>}
                        </div>
                        <div className="shrink-0 rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-2.5">
                          <div className="grid grid-cols-3 divide-x divide-gray-200/80 min-w-[220px]">
                            <div className="px-2.5 text-center">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">MRP</p>
                              <p className={`mt-1 text-sm tabular-nums whitespace-nowrap ${diff > 0 ? "text-gray-400 line-through decoration-gray-300" : "font-medium text-gray-700"}`}>
                                {inr(t.mrp)}
                              </p>
                            </div>
                            <div className="px-2.5 text-center">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-500">AyushPay</p>
                              <p className="mt-1 text-base font-bold tabular-nums text-gray-900 whitespace-nowrap">{inr(t.ayushpay_price)}</p>
                            </div>
                            <div className="px-2.5 text-center">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">You save</p>
                              {diff > 0 ? (
                                <div className="mt-1 flex flex-col items-center gap-1">
                                  <span className="text-sm font-semibold tabular-nums text-emerald-600 whitespace-nowrap">{inr(diff)}</span>
                                  <span className="inline-flex items-center justify-center text-[10px] font-bold leading-none text-emerald-700 bg-emerald-100/80 border border-emerald-200/60 px-2 py-0.5 rounded-full">
                                    {disc}% off
                                  </span>
                                </div>
                              ) : (
                                <p className="mt-1 text-sm text-gray-300">—</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer / pagination */}
            {pages > 1 && (
              <div className="px-6 py-3 bg-white border-t border-gray-100 flex items-center justify-between shrink-0">
                <span className="text-xs text-gray-400">Page {page} of {pages} · {count} total</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages || loading}
                    className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TestsDrawer;